import type { ProductWithHyperguestHotel } from '@travel-planner/shared/schemas';
import type { MapperSearch } from '../model/filters';
import {
  useApplyHyperguestMatches,
  useHyperguestHotels,
  useHyperguestMatched,
  useHyperguestProductHotels,
  useHyperguestSuggest,
  useHyperguestUnconfirmed,
} from '@hyperguest/entities/hyperguest';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Sparkles, Trash2 } from 'lucide-react';
import { useState } from 'react';
import {
  Button,
  Heading,
  Input,
  MultiSelect,
  Tab,
  TabBar,
  Text,
  toastStore,
} from '@/shared/ui';
import { MAPPER_COUNTRY_OPTIONS } from '../model/country-options';
import {
  MAPPER_PAGE_SIZE,
  mergeMapperSearch,
  toFindInput,
} from '../model/filters';
import { usePendingMatchesStore } from '../model/pending-matches-store';
import { mergePendingManualRows } from '../model/sort-pending-first';
import { ApprovalTab } from './approval-tab';
import { HyperguestHotelsTable } from './hyperguest-hotels-table';
import css from './hyperguest-mapper.module.scss';
import { MatchesTab } from './matches-tab';
import { ProductHotelsTable } from './product-hotels-table';

export const HyperguestMapper = () => {
  const search = useSearch({ strict: false }) as MapperSearch;
  const navigate = useNavigate();
  const update = (patch: Partial<MapperSearch>) => {
    const reducer = (prev: MapperSearch) => mergeMapperSearch(prev, patch);
    navigate({
      // biome-ignore lint/suspicious/noExplicitAny: cross-domain route reference
      to: '/management/hyperguest/mapper' as any,
      // biome-ignore lint/suspicious/noExplicitAny: cross-route search reducer
      search: reducer as unknown as any,
      replace: true,
    });
  };

  const tab = search.mapperTab ?? 'matches';
  const productsPage = search.productsPage ?? 1;
  const hotelsPage = search.hotelsPage ?? 1;
  const middlePage = search.middlePage ?? 1;

  const [selectedProductId, setSelectedProductId] = useState<string | null>(
    null
  );
  const [selectedHgExternalId, setSelectedHgExternalId] = useState<
    number | null
  >(null);
  // Cross-pane filtering (legacy parity): the country of the row selected on
  // one side filters the OPPOSITE side. Captured at select-time so the two
  // side queries don't form a mutual data dependency. Cleared on deselect.
  const [selectedProductCountry, setSelectedProductCountry] = useState<
    string | null
  >(null);
  const [selectedHgCountry, setSelectedHgCountry] = useState<string | null>(
    null
  );

  // A single-country override (from the opposite side's selection) wins over
  // the multi-select dropdown for that side only.
  const productsInput = {
    ...toFindInput(search, 'productsPage'),
    countries: selectedHgCountry ? [selectedHgCountry] : search.countries,
  };
  const hotelsInput = {
    ...toFindInput(search, 'hotelsPage'),
    countries: selectedProductCountry
      ? [selectedProductCountry]
      : search.countries,
  };

  const productsQuery = useHyperguestProductHotels(productsInput);
  const hotelsQuery = useHyperguestHotels(hotelsInput);
  const matchedQuery = useHyperguestMatched(toFindInput(search, 'middlePage'));
  const unconfirmedQuery = useHyperguestUnconfirmed(
    toFindInput(search, 'middlePage')
  );

  const apply = useApplyHyperguestMatches();
  const suggest = useHyperguestSuggest();

  const pending = usePendingMatchesStore((s) => s.pending);
  const enqueue = usePendingMatchesStore((s) => s.enqueue);
  const clear = usePendingMatchesStore((s) => s.clear);
  const pendingIds = new Set(pending.keys());
  // Manual matches just queued have no server row yet — surface them as
  // synthetic rows at the top of the Matches tab so the consultant sees the
  // pending pair (dashed border) before Save.
  const pendingManualRows = Array.from(pending.values())
    .filter((m) => m.intent === 'manual' && m.preview)
    .map((m) => m.preview as ProductWithHyperguestHotel);

  // Toggling a row also captures/clears its country for cross-pane filtering,
  // and resets the OPPOSITE side to page 1 (its result set just changed).
  const toggleProduct = (id: string) => {
    if (selectedProductId === id) {
      setSelectedProductId(null);
      setSelectedProductCountry(null);
      update({ hotelsPage: 1 });
      return;
    }
    setSelectedProductId(id);
    setSelectedProductCountry(
      productsQuery.data?.data.find((p) => p.id === id)?.country ?? null
    );
    update({ hotelsPage: 1 });
  };
  const toggleHg = (externalId: number) => {
    if (selectedHgExternalId === externalId) {
      setSelectedHgExternalId(null);
      setSelectedHgCountry(null);
      update({ productsPage: 1 });
      return;
    }
    setSelectedHgExternalId(externalId);
    setSelectedHgCountry(
      hotelsQuery.data?.data.find((h) => h.externalId === externalId)
        ?.country ?? null
    );
    update({ productsPage: 1 });
  };

  const clearSelection = () => {
    setSelectedProductId(null);
    setSelectedHgExternalId(null);
    setSelectedProductCountry(null);
    setSelectedHgCountry(null);
  };

  const [productScores, setProductScores] = useState<Map<string, number>>(
    new Map()
  );
  const [hgScores, setHgScores] = useState<Map<string, number>>(new Map());

  const onSuggestFromProduct = async (productId: string) => {
    const subject = productsQuery.data?.data.find((p) => p.id === productId);
    const candidates = hotelsQuery.data?.data;
    if (!subject || !candidates || candidates.length === 0) return;
    try {
      const res = await suggest.mutateAsync({
        direction: 'product-to-hg',
        subject: {
          id: subject.id,
          title: String(
            (subject.title as { text?: string })?.text ?? subject.id
          ),
          country: subject.country,
          city: subject.city,
        },
        candidates: candidates.map((c) => ({
          id: c.id,
          title: c.title,
          country: c.country,
          city: c.city,
          streetAddress: c.streetAddress,
        })),
        topK: 10,
      });
      const next = new Map<string, number>();
      for (const s of res.suggestions) next.set(s.candidateId, s.similarity);
      setHgScores(next);
    } catch (err) {
      toastStore.getState().show({
        message: `Could not analyze matches: ${err instanceof Error ? err.message : 'Unknown error'}`,
      });
    }
  };

  const onSuggestFromHg = async (hgId: string) => {
    const subject = hotelsQuery.data?.data.find((h) => h.id === hgId);
    const candidates = productsQuery.data?.data;
    if (!subject || !candidates || candidates.length === 0) return;
    try {
      const res = await suggest.mutateAsync({
        direction: 'hg-to-product',
        subject: {
          id: subject.id,
          title: subject.title,
          country: subject.country,
          city: subject.city,
          streetAddress: subject.streetAddress,
        },
        candidates: candidates.map((p) => ({
          id: p.id,
          title: String((p.title as { text?: string })?.text ?? p.id),
          country: p.country,
          city: p.city,
        })),
        topK: 10,
      });
      const next = new Map<string, number>();
      for (const s of res.suggestions) next.set(s.candidateId, s.similarity);
      setProductScores(next);
    } catch (err) {
      toastStore.getState().show({
        message: `Could not analyze matches: ${err instanceof Error ? err.message : 'Unknown error'}`,
      });
    }
  };

  const onManualSave = () => {
    if (!selectedProductId || selectedHgExternalId === null) return;
    const productHotel = productsQuery.data?.data.find(
      (p) => p.id === selectedProductId
    );
    const hyperguestHotel = hotelsQuery.data?.data.find(
      (h) => h.externalId === selectedHgExternalId
    );
    enqueue(selectedProductId, {
      intent: 'manual',
      hyperGuestHotelId: selectedHgExternalId,
      unconfirmedHyperGuestId: null,
      // Resolve the pair now so it can render as a pending row at the top of
      // the Matches tab before Save — both rows leave the side panes once
      // queued, so we can't re-derive them from query data later.
      preview:
        productHotel && hyperguestHotel
          ? { productHotel, hyperguestHotel }
          : undefined,
    });
    clearSelection();
  };

  const onApplyAll = async () => {
    if (pending.size === 0) return;
    try {
      await apply.mutateAsync({
        items: Array.from(pending.entries()).map(([productId, m]) => ({
          productId,
          hyperGuestHotelId: m.hyperGuestHotelId,
          unconfirmedHyperGuestId: m.unconfirmedHyperGuestId,
        })),
      });
      toastStore.getState().show({
        message: `Applied ${pending.size} match${pending.size === 1 ? '' : 'es'}`,
      });
      clear();
      setProductScores(new Map());
      setHgScores(new Map());
    } catch (err) {
      toastStore.getState().show({
        message: `Failed to apply matches: ${err instanceof Error ? err.message : 'Unknown error'}`,
      });
    }
  };

  const productsTotalPages = Math.max(
    1,
    Math.ceil((productsQuery.data?.countAll ?? 0) / MAPPER_PAGE_SIZE)
  );
  const hotelsTotalPages = Math.max(
    1,
    Math.ceil((hotelsQuery.data?.countAll ?? 0) / MAPPER_PAGE_SIZE)
  );
  const middleQuery = tab === 'matches' ? matchedQuery : unconfirmedQuery;
  const middleTotalPages = Math.max(
    1,
    Math.ceil((middleQuery.data?.countAll ?? 0) / MAPPER_PAGE_SIZE)
  );

  return (
    <div className={css.root}>
      <div className={css.toolbar}>
        <div className={css.toolbarFilters}>
          <div className={css.toolbarSearch}>
            <Input
              placeholder="Search by title…"
              value={search.search ?? ''}
              onChange={(e) =>
                update({
                  search: e.target.value,
                  productsPage: 1,
                  hotelsPage: 1,
                  middlePage: 1,
                })
              }
            />
          </div>
          <MultiSelect
            className={css.toolbarCountry}
            options={MAPPER_COUNTRY_OPTIONS}
            placeholder="All countries"
            searchPlaceholder="Search countries…"
            showSelectAll={false}
            usePortal
            values={search.countries ?? []}
            onChange={(countries) =>
              update({
                countries,
                productsPage: 1,
                hotelsPage: 1,
                middlePage: 1,
              })
            }
          />
          <Button
            variant="outline"
            icon={<Trash2 style={{ height: 16, width: 16 }} />}
            aria-label="Clear all filters"
            title="Clear all filters"
            onClick={() =>
              update({
                search: undefined,
                countries: undefined,
                productsPage: 1,
                hotelsPage: 1,
                middlePage: 1,
              })
            }
            disabled={!search.search && !(search.countries?.length ?? 0)}
          />
        </div>
        <div className={css.toolbarRight}>
          <Button variant="ghost" onClick={clear} disabled={pending.size === 0}>
            Cancel ({pending.size})
          </Button>
          <Button
            variant="primary"
            onClick={onApplyAll}
            disabled={pending.size === 0 || apply.isPending}
          >
            {apply.isPending ? 'Saving…' : `Save ${pending.size || ''}`}
          </Button>
        </div>
      </div>

      {selectedProductId && selectedHgExternalId !== null && (
        <div className={css.manualSaveBanner}>
          <Text size="sm">
            Product + HyperGuest hotel selected — queue a manual match?
          </Text>
          <div className={css.manualSaveActions}>
            <Button size="sm" variant="ghost" onClick={clearSelection}>
              Clear
            </Button>
            <Button size="sm" variant="primary" onClick={onManualSave}>
              Queue manual match
            </Button>
          </div>
        </div>
      )}

      <div className={css.threePane}>
        <section>
          <div className={css.paneHeader}>
            <Heading as="h2" size="md">
              Product hotels (unmatched)
            </Heading>
            {selectedProductId && (
              <Button
                size="sm"
                variant="ghost"
                icon={<Sparkles style={{ height: 14, width: 14 }} />}
                disabled={suggest.isPending}
                onClick={() => onSuggestFromProduct(selectedProductId)}
              >
                {suggest.isPending ? 'Analyzing…' : 'AI Match'}
              </Button>
            )}
          </div>
          <ProductHotelsTable
            rows={productsQuery.data?.data ?? []}
            totalPages={productsTotalPages}
            currentPage={productsPage}
            selectedId={selectedProductId}
            scores={productScores}
            loading={productsQuery.isLoading}
            onSelect={toggleProduct}
            onPageChange={(p) => update({ productsPage: p })}
          />
        </section>

        <section>
          <div className={css.paneHeader}>
            <TabBar>
              <Tab
                active={tab === 'matches'}
                onClick={() => update({ mapperTab: undefined, middlePage: 1 })}
              >
                Matches
              </Tab>
              <Tab
                active={tab === 'approval'}
                onClick={() => update({ mapperTab: 'approval', middlePage: 1 })}
              >
                Approval
              </Tab>
            </TabBar>
            {pending.size > 0 && (
              <Text size="xs" variant="muted">
                {pending.size} pending
              </Text>
            )}
          </div>
          {tab === 'matches' ? (
            <MatchesTab
              rows={mergePendingManualRows(
                matchedQuery.data?.data ?? [],
                pendingManualRows
              )}
              totalPages={middleTotalPages}
              currentPage={middlePage}
              loading={matchedQuery.isLoading}
              pendingIds={pendingIds}
              onUnmatch={(productId) =>
                enqueue(productId, {
                  intent: 'reject',
                  hyperGuestHotelId: null,
                  unconfirmedHyperGuestId: null,
                })
              }
              onPageChange={(p) => update({ middlePage: p })}
            />
          ) : (
            <ApprovalTab
              rows={unconfirmedQuery.data?.data ?? []}
              totalPages={middleTotalPages}
              currentPage={middlePage}
              loading={unconfirmedQuery.isLoading}
              pendingIds={pendingIds}
              onConfirm={(productId, hgId) =>
                enqueue(productId, {
                  intent: 'confirm',
                  hyperGuestHotelId: hgId,
                  unconfirmedHyperGuestId: null,
                })
              }
              onReject={(productId) =>
                enqueue(productId, {
                  intent: 'reject',
                  hyperGuestHotelId: null,
                  unconfirmedHyperGuestId: null,
                })
              }
              onPageChange={(p) => update({ middlePage: p })}
            />
          )}
        </section>

        <section>
          <div className={css.paneHeader}>
            <Heading as="h2" size="md">
              HyperGuest hotels
            </Heading>
            {selectedHgExternalId !== null && (
              <Button
                size="sm"
                variant="ghost"
                icon={<Sparkles style={{ height: 14, width: 14 }} />}
                disabled={suggest.isPending}
                onClick={() => {
                  const hg = hotelsQuery.data?.data.find(
                    (h) => h.externalId === selectedHgExternalId
                  );
                  if (hg) onSuggestFromHg(hg.id);
                }}
              >
                {suggest.isPending ? 'Analyzing…' : 'AI Match'}
              </Button>
            )}
          </div>
          <HyperguestHotelsTable
            rows={hotelsQuery.data?.data ?? []}
            totalPages={hotelsTotalPages}
            currentPage={hotelsPage}
            selectedExternalId={selectedHgExternalId}
            scores={hgScores}
            loading={hotelsQuery.isLoading}
            onSelect={toggleHg}
            onPageChange={(p) => update({ hotelsPage: p })}
          />
        </section>
      </div>
    </div>
  );
};
